import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

const hinduOfTheYearCategories = [
  {
    title: 'Active Youth',
    summary: 'Recognising young Hindus, ages 18 to 35, who translate Vedantic wisdom into lived action.',
    intro: [
      'This award recognises young Hindus, ages 18 to 35, who translate Vedantic wisdom into lived action by demonstrating ethical conduct, selfless service, and responsible leadership.',
      'Nominees will be evaluated on the depth, sincerity, and impact of their contributions, with attention to how effectively they embody Hindu values through active engagement, leadership, and service.',
      'Selection will be based on qualitative insight and verifiable evidence of their contributions to preserve Dharma and their leadership as torch bearers for Dharma.'
    ],
    criteria: [
      'Youth leadership, initiative, and responsibility, including the ability to lead, inspire, and take responsibility within youth spaces or community settings.',
      'Alignment with Hindu and Vedantic values, showing Dharma as a lived responsibility.',
      'Innovation, creativity, and relevance, including the ability to apply Hindu values meaningfully in contemporary youth and community contexts.',
      'Reach, sustainability, and long-term impact, including inclusivity, continuity, collaboration, and selfless seva.',
      'Integrity, character, and role-model potential for Hindu youth.'
    ]
  },
  {
    title: 'Arts, Culture, and Language',
    summary: 'Honouring sustained contribution to Hindu arts, culture, language, and creative expression.',
    intro: [
      'Nominees must have a proven track record in the field, displaying a minimum of five years of active participation and/or voluntary service in the relevant subcategory.'
    ],
    criteria: [
      'Production of original literature or creative works.',
      'Use of arts and cultural platforms to actively promote Hinduism.',
      'Involvement in the production of drama, films, or other media forms.',
      'Active participation in, or association with, credible organisations dedicated to arts, culture, and language in South Africa.',
      'Artistic contribution through drama, music, dance, painting, sculpture, graphic arts, or related media.',
      'Engagement with all linguistic groups within the South African Indian diaspora.',
      'Appropriate educational or skills-based qualifications in the designated category.',
      'Participation in, or organisation of, national and/or international conferences, symposiums, or similar events.'
    ]
  },
  {
    title: 'Business Leadership and Entrepreneurship',
    summary: 'Celebrating Hindu entrepreneurs and business leaders who pair success with upliftment.',
    intro: [
      'This award honours Hindu entrepreneurs or business leaders who have achieved success while significantly contributing to community upliftment, economic growth, or empowering others in South Africa.',
      'The entrant can be a professional in a solo practice or a leader in an organisational context, and must have proper registration within the South African context.',
      'This award will be split into male, female, and youth under 35.'
    ],
    criteria: [
      'Business success within the scope of Dharmic principles, including good standing, righteous conduct, good governance, and evidence of sustained profitability.',
      'Upliftment of vulnerable communities at large and Hindu communities in particular, with a focus on cultural preservation and empowering Hindu individuals or youth in South Africa.',
      'Promotion of empowerment and growth through business initiatives, job creation, collaboration, CSI initiatives, professional development, and skills development.',
      'Seva in business through philanthropy, mentorship, or support initiatives reflecting Hindu principles.'
    ]
  },
  {
    title: 'Health, Wellness, Social Welfare, and Education',
    summary: 'Recognising transformative service through care, welfare, education, and wellbeing.',
    intro: [
      'This award honours an individual who has demonstrated exceptional guidance, unwavering devotion, and transformative service to the Hindu community he or she identifies with.',
      'This category recognises those who lead through the lens of Sanatana Dharma and provide a moral compass for the community in a modern South African context.',
      'The award can be granted to an individual making an impact at a small scale or large scale, especially through living the teachings of our scriptures in health, wellness, social welfare, and education.'
    ],
    criteria: [
      'Health and wellness contributions, including innovation in healthcare, public health impact, holistic wellness, Yoga, Ayurveda, mental health support, and reduced social stigma.',
      'Social welfare rooted in seva, including community development, rural empowerment, sanitation, disaster relief, and upliftment of vulnerable groups.',
      'Sustainable practices, including environmental protection, wildlife conservation, water-saving campaigns, Gau Seva, and stray animal support.',
      'Education contributions, including literacy access, support for underprivileged learners, cultural and spiritual education, skills development, scholarships, sponsorships, and digital infrastructure.'
    ]
  },
  {
    title: 'Hindu Spiritual Leadership and Community Upliftment',
    summary: 'Honouring spiritual guidance, Dharmic leadership, and service to the community.',
    intro: [
      'This award honours an individual who has demonstrated exceptional guidance, unwavering devotion, and transformative service to the Hindu community he or she identifies with.',
      'The category recognises those who lead through the lens of Sanatana Dharma, foster spiritual growth, preserve Vedic heritage, and provide a moral compass in a modern South African context.',
      'Nominees must have demonstrated active spiritual leadership or contribution for the year and must not have been involved in negative media publicity for the last five years.'
    ],
    criteria: [
      'Propagation of and alignment to scriptures, including living, teaching, mentoring, or leading by example in line with scriptural teachings.',
      'Community building and unity through inter-organisational collaboration across linguistic or sectarian lines.',
      'Youth engagement that inspires younger generations to embrace spiritual heritage and participate in temples or community activities.',
      'Conflict resolution using spiritual principles to mediate disputes and promote peace.',
      'Embodiment of Vedic and Dharmic values through personal integrity and selfless service.',
      'Impact and innovation, including legacy, reach, sincerity, vision, and relevance to contemporary South African challenges.'
    ]
  },
  {
    title: 'Women, Children, and Youth Empowerment',
    summary: 'Recognising work that advances empowerment, resilience, advocacy, and capacity building.',
    intro: [
      'Nominees may operate in women empowerment, children empowerment, youth empowerment, or across all three spaces.',
      'Nominees will be evaluated on the depth, sincerity, and impact of their contributions, with attention to how effectively they embody ethical and Dharmic values through active engagement, leadership, and service.'
    ],
    criteria: [
      'Clear definition or elaboration of projects, with a portfolio of impact and outcomes from implemented projects or programmes.',
      'Demonstrable systemic progress, including before-and-after mapping where applicable.',
      'Networking, support, and partnerships that advance the cause in workplaces, schools, and communities.',
      'Awareness creation on issues affecting women, children, and youth through projects, programmes, advocacy, and advancement.',
      'Resilience in overcoming challenges and obstacles related to empowerment initiatives.',
      'Evidence of training and capacity-building initiatives.',
      'Good governance in project implementation, including record keeping, auditable financial information, and compliance with legal frameworks.'
    ]
  }
];

const generalEntryRules = [
  'Age: Nominees must be 18 years or older.',
  'Community Service: A minimum of five years of active service within the relevant categories is required.',
  'Verification: All credentials submitted will be subject to external verification.',
  'Hindu Ethos: The nominee’s contributions must distinctly reflect Hindu values and principles.',
  'Personal Conduct: The nominees must be of sober habits, with no pending or past criminal records or judgments.',
  'Community Focus: Contributions must benefit South African communities, with a focus on Hindus, and should have a significant impact.'
];

const categoryDownloads = [
  {
    title: 'Active Youth',
    href: '/hoy-active-youth.docx'
  },
  {
    title: 'Arts, Culture, and Language',
    href: '/hoy-arts-culture-language.docx'
  },
  {
    title: 'Business Leadership and Entrepreneurship',
    href: '/hoy-business-leadership-entrepreneurship.docx'
  },
  {
    title: 'Health, Wellness, Social Welfare, and Education',
    href: '/hoy-health-wellness-social-welfare-education.docx'
  },
  {
    title: 'Hindu Spiritual Leadership and Community Upliftment',
    href: '/hoy-hindu-spiritual-leadership-community-upliftment.docx'
  },
  {
    title: 'Women, Children, and Youth Empowerment',
    href: '/hoy-women-children-youth-empowerment.docx'
  }
];

const trackEntryFormDownload = (location) => {
  fetch('/api/downloads/hoy-entry-form', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ location })
  }).catch(() => {
    // Downloads should still work if the counter API is unavailable.
  });
};

function UpcomingEvent() {
  const [selectedCategory, setSelectedCategory] = useState(null);

  useEffect(() => {
    if (!selectedCategory) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setSelectedCategory(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedCategory]);

  return (
    <div className="fade-in upcoming-events-page">
      <div className="hero">
        <h1>Upcoming Events</h1>
        <p>Join us for our next community gathering and be part of the Shantik Foundation journey.</p>
      </div>

      <div className="content-section container event-section hoty-section">
        <h2>2026 Hindu of the Year</h2>

        <p className="event-intro">
          The 2026 Hindu of the Year awards recognise individuals whose work reflects Dharma,
          service, leadership, and upliftment across key areas of community life.
        </p>

        <div className="entry-download-panel">
          <div>
            <h3>Entry Form</h3>
            <p>Download the shared Word entry form for all 2026 Hindu of the Year subcategories.</p>
            <p className="entry-download-disclaimer">
              For all enquires regarding 2026 Hindu of the Year or and submit your form please send
              an email to admin@shantik.co.za
            </p>
          </div>
          <a
            href="/hoy-entry-form.docx"
            className="btn"
            download
            onClick={() => trackEntryFormDownload('top')}
          >
            Download Entry Form
          </a>
        </div>



        <div className="event-category-grid">
          {hinduOfTheYearCategories.map((category) => (
            <button
              type="button"
              className="card event-category-card"
              key={category.title}
              onClick={() => setSelectedCategory(category)}
            >
              <span className="event-category-label">Subcategory</span>
              <h3>{category.title}</h3>
              <p>{category.summary}</p>
              <span className="event-card-action">Read more</span>
            </button>
          ))}
        </div>

        <div className="entry-download-panel">
          <div>
            <h3>Entry Form</h3>
            <p>Download the shared Word entry form for all 2026 Hindu of the Year subcategories.</p>
          </div>
          <a
            href="/hoy-entry-form.docx"
            className="btn"
            download
            onClick={() => trackEntryFormDownload('main')}
          >
            Download Entry Form
          </a>
        </div>

        <div className="category-downloads">
          <h3>Subcategory Information Downloads</h3>
          <div className="category-download-grid">
            {categoryDownloads.map((download) => (
              <a
                href={download.href}
                className="category-download-link"
                download
                key={download.href}
              >
                {download.title}
              </a>
            ))}
          </div>
        </div>

        <div className="entry-rules">
          <h3>General Entry Rules</h3>
          <ul>
            {generalEntryRules.map((rule) => (
              <li key={rule}>{rule}</li>
            ))}
          </ul>
          <p>
            Nomination / entry forms together with acceptance of nomination by the nominee must be
            forwarded together with a portfolio of evidence, such as a USB, file, and relevant and
            appropriate supporting documents.
          </p>
          <p>
            Self-nominations are accepted. In this instance the nominee acceptance form is not a
            requirement.
          </p>
          <p>
            Certified copies of ID documents, work permits, passports, or equivalent documents must
            also accompany the nomination.
          </p>
        </div>
      </div>

      <div className="content-section container event-section">
        <h2>Past Events</h2>
        <div className="program-card event-card">
          <h3>Swami Vivekananda Memorial Lecture</h3>
          <p>
            The Foundation hosted the Swami Vivekananda Memorial Lecture as part of its 2026
            community programme.
          </p>
          <p>
            <strong>22 February 2026</strong> - Swami Vivekananda Memorial Lecture at our Chatsworth branch.
          </p>
        </div>
      </div>

      <div className="content-section container event-section">
        <h2>More Events Planned</h2>
        <div className="cards-container">
          <div className="card event-detail-card">
            <h3>Home Industry Showcase</h3>
            <p>
              Later in the year, there will be a home industry showcase inviting our members to display
              and sell goods they have made.
            </p>
          </div>
          <div className="card event-detail-card">
            <h3>Stay Involved</h3>
            <p>Contact us to learn more about attendance, participation, or supporting these upcoming events.</p>
          </div>
        </div>
      </div>

      <div className="content-section container">
        <h2>Stay Connected</h2>
        <p>Contact us if you would like early information about the next event or how to participate.</p>
        <Link to="/contact" className="btn">Contact Us</Link>
        <Link to="/gallery" className="btn btn-secondary">View Past Highlights</Link>
      </div>

      {selectedCategory && (
        <div
          className="event-modal"
          role="presentation"
          onMouseDown={() => setSelectedCategory(null)}
        >
          <div
            className="event-modal-card"
            role="dialog"
            aria-modal="true"
            aria-labelledby="event-category-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="event-modal-close"
              aria-label="Close category details"
              onClick={() => setSelectedCategory(null)}
            >
              &times;
            </button>
            <span className="event-category-label">2026 Hindu of the Year</span>
            <h2 id="event-category-title">{selectedCategory.title}</h2>
            {selectedCategory.intro.map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
            <h3>Selection Criteria</h3>
            <ul>
              {selectedCategory.criteria.map((criterion) => (
                <li key={criterion}>{criterion}</li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

export default UpcomingEvent;
